import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import find from 'lodash/find'
import hasElementOrSomeParentTheClass from '@Utils/HasElementOrSomeParentTheClass'
import Overlay from '@SiteComponents/Overlay'
import LanguagesViewHeader from './LanguagesViewHeader'
import LanguagesTable from './LanguagesTable'
import Close from '@Images/languages_view_close.svg'
import Expand from '@Images/languages_view_expand.svg'
import Next from '@Images/languages_view_next.svg'
import Previous from '@Images/languages_view_previous.svg'

import {
  IProject,
} from '@SiteModules/Project/types'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  IDeleteLanguagePayload,
} from '@SiteModules/Subtitle/types'

import {
  IOpenImportSrtPopupPayload,
  IOpenExternalTokenPopupPayload,
} from '@SiteModules/Popup/types'

import {
  getProjectsWithLanguages,
  getProjectLabelCaptions,
} from '@SiteModules/Project/selectors'

import {
  getCurrentAccountHasCurrentSubscription
} from '@SiteModules/Account/selectors'

import {
  getAnyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen
} from '@SiteModules/Popup/selectors'

import {
  openImportSrtPopup,
  openExternalTokenPopup,
  openSetupProjectPopup,
  openSetupProjectPopupTts,
  openRenewSubscriptionPopup,
} from '@SiteModules/Popup/actions'

import {
  deleteLanguage
} from '@SiteModules/Subtitle/actions'

import './LanguagesView.scss'

interface LanguagesViewProps {
  readonly projectVersion: IProjectVersion | undefined
  readonly project: IProject | undefined
  readonly fullLanguagesView: boolean
  readonly shouldRender: boolean
  resetOpenedProjectVersion(): void
  setOpenedProjectVersionId(projectId: string): void
  setFullLanguagesView(newFullLanguagesView: boolean): void
}

const LanguagesView: React.FC<LanguagesViewProps> = props => {
  const dispatch = useDispatch()

  const [hasInitializedFirstOpenedProjectHasMoreThanOneProjectVersion, setHasInitializedFirstOpenedProjectHasMoreThanOneProjectVersion] = useState(false)

  const projectsWithLanguages = useSelector(getProjectsWithLanguages)
  const projectLabelCaptions = useSelector(getProjectLabelCaptions)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)
  const anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen = useSelector(getAnyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen)

  const {
    fullLanguagesView,
    projectVersion,
    project,
    shouldRender,
  } = props

  const halfLanguagesView = !fullLanguagesView && shouldRender
  const shouldRenderOverlay = halfLanguagesView
  const allProjectVersionsOfProject = project ? project.projectVersions : []

  const projectsWithLanguagesRef = useRef(projectsWithLanguages)
  const allProjectVersionsOfProjectRef = useRef(allProjectVersionsOfProject)
  const firstOpenedProjectHasMoreThanOneProjectVersionRef = useRef(false)
  const indexOfOpenedProject = project ? projectsWithLanguages.indexOf(project) : -1
  const indexOfOpenedProjectRef = useRef(indexOfOpenedProject)
  const indexOfOpenedProjectVersion = project && projectVersion ? allProjectVersionsOfProject.indexOf(projectVersion) : -1
  const indexOfOpenedProjectVersionRef = useRef(indexOfOpenedProjectVersion)
  const fullLanguagesViewRef = useRef(fullLanguagesView)
  const anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpenRef = useRef(anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen)

  const isFirstProjectVersionOfProject = indexOfOpenedProjectVersion === 0
  const isLastProjectVersionOfProject = indexOfOpenedProjectVersion === allProjectVersionsOfProject.length - 1
  const isFirstProjectWithLanguages = indexOfOpenedProject === 0
  const isLastProjectWithLanguages = indexOfOpenedProject === projectsWithLanguages.length - 1

  const isFirst = firstOpenedProjectHasMoreThanOneProjectVersionRef.current ? isFirstProjectVersionOfProject : isFirstProjectWithLanguages
  const isLast = firstOpenedProjectHasMoreThanOneProjectVersionRef.current ? isLastProjectVersionOfProject : isLastProjectWithLanguages

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fullLanguagesViewRef.current || anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpenRef.current) { return }

      if (e.target) {
        const element = e.target as HTMLElement
        if (hasElementOrSomeParentTheClass(element, 'LanguagesView__element')) {
          return
        }
        props.resetOpenedProjectVersion()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    if (shouldRender && !hasInitializedFirstOpenedProjectHasMoreThanOneProjectVersion) {
      if (project && project.projectVersions.length > 1) {
        firstOpenedProjectHasMoreThanOneProjectVersionRef.current = true
      }

      setHasInitializedFirstOpenedProjectHasMoreThanOneProjectVersion(true)
    }

    if (!shouldRender) {
      setHasInitializedFirstOpenedProjectHasMoreThanOneProjectVersion(false)
    }
  }, [shouldRender])

  useEffect(() => {
    indexOfOpenedProjectRef.current = indexOfOpenedProject
  }, [indexOfOpenedProject])

  useEffect(() => {
    indexOfOpenedProjectVersionRef.current = indexOfOpenedProjectVersion
  }, [indexOfOpenedProjectVersion])

  useEffect(() => {
    projectsWithLanguagesRef.current = projectsWithLanguages
  }, [projectsWithLanguages])

  useEffect(() => {
    allProjectVersionsOfProjectRef.current = allProjectVersionsOfProject
  }, [allProjectVersionsOfProject])

  useEffect(() => {
    fullLanguagesViewRef.current = fullLanguagesView
  }, [fullLanguagesView])

  useEffect(() => {
    anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpenRef.current = anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen
  }, [anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen])

  const isArrowUp = (event: KeyboardEvent) => {
    return event.key === 'ArrowUp'
  }

  const isArrowDown = (event: KeyboardEvent) => {
    return event.key === 'ArrowDown'
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isArrowUp(event)) {
      event.preventDefault()
      setPrevious()
    }

    if (isArrowDown(event)) {
      event.preventDefault()
      setNext()
    }
  }

  const handleOpenImportSrtPopup = (payload: IOpenImportSrtPopupPayload) => {
    dispatch(openImportSrtPopup(payload))
  }

  const handleOpenExternalTokenPopup = (payload: IOpenExternalTokenPopupPayload) => {
    dispatch(openExternalTokenPopup(payload))
  }

  const handleOpenSetupProjectOrRenewSubscriptionPopup = () => {
    if (!projectVersion) { return }

    if (currentAccountHasCurrentSubscription && projectVersion.attributes.belongsToTtsProject) {
      dispatch(openSetupProjectPopupTts({ projectVersionIds: [projectVersion.id] }))
    } else if (currentAccountHasCurrentSubscription) {
      dispatch(openSetupProjectPopup({ projectVersionIds: [projectVersion.id] }))
    } else {
      dispatch(openRenewSubscriptionPopup())
    }
  }

  const handleDeleteLanguage = (payload: IDeleteLanguagePayload) => {
    dispatch(deleteLanguage(payload))
  }

  const setNext = () => {
    firstOpenedProjectHasMoreThanOneProjectVersionRef.current ? setNextProjectVersionOfSameProject() : setNextOriginalProjectVersionWithLanguages()
  }

  const setPrevious = () => {
    firstOpenedProjectHasMoreThanOneProjectVersionRef.current ? setPreviousProjectVersionOfSameProject() : setPreviousOriginalProjectVersionWithLanguages()
  }

  const setNextProjectVersionOfSameProject = () => {
    if (indexOfOpenedProjectVersionRef.current < allProjectVersionsOfProjectRef.current.length - 1 && indexOfOpenedProjectVersionRef.current > -1) {
      const nextProjectVersionOfSameProject = allProjectVersionsOfProjectRef.current[indexOfOpenedProjectVersionRef.current + 1]
      props.setOpenedProjectVersionId(nextProjectVersionOfSameProject.id)
    }
  }

  const setPreviousProjectVersionOfSameProject = () => {
    if (indexOfOpenedProjectVersionRef.current > 0) {
      const previousProjectVersionOfSameProject = allProjectVersionsOfProjectRef.current[indexOfOpenedProjectVersionRef.current - 1]
      props.setOpenedProjectVersionId(previousProjectVersionOfSameProject.id)
    }
  }

  const setNextOriginalProjectVersionWithLanguages = () => {
    if (indexOfOpenedProjectRef.current < projectsWithLanguagesRef.current.length - 1 && indexOfOpenedProjectRef.current > -1) {
      const nextProjectWithLanguages = projectsWithLanguagesRef.current[indexOfOpenedProjectRef.current + 1]
      const originalProjectVersionOfNextProjectWithLanguages = find(nextProjectWithLanguages.projectVersions, pv => !pv.attributes.isRepurposed)

      if (originalProjectVersionOfNextProjectWithLanguages) {
        props.setOpenedProjectVersionId(originalProjectVersionOfNextProjectWithLanguages.id)
      }
    }
  }

  const setPreviousOriginalProjectVersionWithLanguages = () => {
    if (indexOfOpenedProjectRef.current > 0) {
      const previousProjectWithLanguages = projectsWithLanguagesRef.current[indexOfOpenedProjectRef.current - 1]
      const originalProjectVersionOfPreviousProjectWithLanguages = find(previousProjectWithLanguages.projectVersions, pv => !pv.attributes.isRepurposed)

      if (originalProjectVersionOfPreviousProjectWithLanguages) {
        props.setOpenedProjectVersionId(originalProjectVersionOfPreviousProjectWithLanguages.id)
      }
    }
  }

  const renderActionsHalfView = () => {
    return (
      <div className="LanguagesView__actions">
        <Close
          className="LanguagesView__actions-close"
          onClick={props.resetOpenedProjectVersion}
        />
        <Expand
          className="LanguagesView__actions-expand"
          onClick={props.setFullLanguagesView}
        />
        <div className="LanguagesView__actions-separator"/>
        <Next
          className={classnames(
            'LanguagesView__actions-next',
            { 'LanguagesView__actions-next-disabled': isLast }
          )}
          onClick={setNext}
        />
          <Previous
            className={classnames(
              'LanguagesView__actions-next',
              { 'LanguagesView__actions-previous-disabled': isFirst }
            )}
            onClick={setPrevious}
          />
      </div>
    )
  }

  const renderHeader = () => {
    return (
      <LanguagesViewHeader
        fullLanguagesView={fullLanguagesView}
        projectVersion={projectVersion}
        project={project}
        projectLabelCaptions={projectLabelCaptions}
        resetOpenedProjectVersion={props.resetOpenedProjectVersion}
        openSetupProjectOrRenewSubscriptionPopup={handleOpenSetupProjectOrRenewSubscriptionPopup}
      />
    )
  }

  const renderLanguagesTable = () => {
    return (
      <LanguagesTable
        projectVersion={projectVersion as IProjectVersion}
        fullLanguagesView={fullLanguagesView}
        handleOpenImportSrtPopup={handleOpenImportSrtPopup}
        handleOpenExternalTokenPopup={handleOpenExternalTokenPopup}
        handleDeleteLanguage={handleDeleteLanguage}
      />
    )
  }

  const renderPreparingClips = () => {
    return (
      <div className="LanguagesView__preparing-clips">
        <div className="LanguagesView__preparing-clips-main">
          <div className="LanguagesView__preparing-clips-orb" aria-hidden="true">
            <span>AI</span>
          </div>
          <div className="LanguagesView__preparing-clips-copy">
            <div className="LanguagesView__preparing-clips-label">
              Generating clips
            </div>
            <div className="LanguagesView__preparing-clips-title" role="heading" aria-level={2}>
              Watching and listening to your video
            </div>
            <div className="LanguagesView__preparing-clips-body" role="status" aria-live="polite">
              We are generating the transcript that the clip model needs before it can find the
              strongest moments. You can leave this page; we will keep working.
            </div>
          </div>
        </div>
        <ul className="LanguagesView__preparing-clips-steps" aria-label="Clip preparation sequence">
          <li
            className="LanguagesView__preparing-clips-step LanguagesView__preparing-clips-step-active"
            aria-current="step"
          >
            <span className="LanguagesView__preparing-clips-step-dot" aria-hidden="true" />
            <span>Transcribing source</span>
          </li>
          <li className="LanguagesView__preparing-clips-step">
            <span className="LanguagesView__preparing-clips-step-dot" aria-hidden="true" />
            <span>Selecting highlights</span>
          </li>
          <li className="LanguagesView__preparing-clips-step">
            <span className="LanguagesView__preparing-clips-step-dot" aria-hidden="true" />
            <span>Crafting clips</span>
          </li>
        </ul>
        <div className="LanguagesView__preparing-clips-preview" aria-hidden="true">
          <span className="LanguagesView__preparing-clips-preview-card" />
          <span className="LanguagesView__preparing-clips-preview-card" />
          <span className="LanguagesView__preparing-clips-preview-card" />
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      {shouldRenderOverlay && (
        <Overlay />
      )}
      <div className={classnames(
        'LanguagesView',
        'LanguagesView__element',
        { 'LanguagesView__hidden': !shouldRender },
        { 'LanguagesView__full': fullLanguagesView }
      )}>
        {halfLanguagesView && renderActionsHalfView()}
        {renderHeader()}
        {projectVersion && (
          projectVersion.attributes.isPreparingRepurposedClips ? renderPreparingClips() : renderLanguagesTable()
        )}
      </div>
    </React.Fragment>
  )
}

export default LanguagesView
